import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  ButtonBase,
  Collapse,
  Dialog,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import {
  SettingsGroup,
  Tick,
  rowSx,
} from "@dashboard/components/common/settingsRows";
import { FilterDropdown } from "@dashboard/components/menu-urls/FilterDropdown";
import {
  PREVIEW_MODES,
  type PreviewMode,
} from "@dashboard/components/themes/previewMode";
import {
  focusRing,
  reducedMotion,
} from "@dashboard/components/themes/themeMotion";
import { useDashboardAppFeatureFlag } from "@dashboard/hooks/useFeature";
import {
  type GuestMenuVersion,
  useGuestMenuVersion,
} from "@dashboard/hooks/useGuestMenuVersion";

import { LazyGuestMenuVersionPreview } from "./LazyGuestMenuVersionPreview";
import { SegmentedControl, useRadioArrowKeys } from "./SegmentedControl";
import { SheetButton } from "./SheetButton";
import { SwitchBackSheet } from "./SwitchBackSheet";
import { trackSwitchBackCancelled } from "./guestMenuEvents";
import { SHEET_TRANSITION_MS, sheetBackdrop, sheetPaper } from "./sheetStyles";
import { useMenuUrlChoice } from "./useMenuUrlChoice";
import { useSwitchGuestMenu } from "./useSwitchGuestMenu";

// Long enough to see the tick land before the modal goes.
const CLOSE_AFTER_SWITCH_MS = 900;

const versionName = (version: GuestMenuVersion) =>
  version === "new" ? "the new menu" : "Classic";

type GuestMenuPreviewDialogProps = {
  open: boolean;
  onClose: () => void;
  /** The version shown first; New unless the caller is showing Classic. */
  initialShown?: GuestMenuVersion;
  /** Runs once the restaurant has switched from here. */
  onSwitched?: (to: GuestMenuVersion) => void;
  /** Closes itself shortly after a switch, so the page behind is seen changing. */
  closeAfterSwitch?: boolean;
  /** Previews this theme, starting on a menu URL that uses it. */
  themeId?: string;
};

/**
 * The restaurant's real guest menu on a phone-sized stage, in either version,
 * with the switch between them. Opened from Themes, Settings, the header and
 * the Menus notice, so all of them preview and switch the same way.
 */
export const GuestMenuPreviewDialog = ({
  open,
  onClose,
  initialShown = "new",
  onSwitched,
  closeAfterSwitch = false,
  themeId,
}: GuestMenuPreviewDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="guest-menu-preview-title"
      transitionDuration={SHEET_TRANSITION_MS}
      maxWidth={false}
      PaperProps={{
        sx: [
          sheetPaper,
          {
            width: 1120,
            height: "min(860px, calc(100% - 32px))",
            maxHeight: "none",
            borderRadius: "18px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            "@keyframes guestMenuPreviewIn": {
              from: { transform: "scale(0.96)" },
              to: { transform: "scale(1)" },
            },
            animation: "guestMenuPreviewIn 250ms ease-out",
          },
          reducedMotion,
        ],
      }}
      slotProps={{
        backdrop: { sx: sheetBackdrop },
      }}
    >
      {open && (
        <PreviewContent
          onClose={onClose}
          initialShown={initialShown}
          onSwitched={onSwitched}
          closeAfterSwitch={closeAfterSwitch}
          themeId={themeId}
        />
      )}
    </Dialog>
  );
};

const PreviewContent = ({
  onClose,
  initialShown,
  onSwitched,
  closeAfterSwitch,
  themeId,
}: {
  onClose: () => void;
  initialShown: GuestMenuVersion;
  onSwitched?: (to: GuestMenuVersion) => void;
  closeAfterSwitch: boolean;
  themeId?: string;
}) => {
  const live = useGuestMenuVersion();
  // Restaurants not yet offered the new menu only ever see Classic here.
  const offered =
    useDashboardAppFeatureFlag("enableGuestMenuV2") || live === "new";
  const { menuUrl, docId, setDocId, options } = useMenuUrlChoice(themeId);
  const [shown, setShown] = useState<GuestMenuVersion>(
    offered ? initialShown : "classic"
  );
  const [mode, setMode] = useState<PreviewMode>("light");
  const [saving, setSaving] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [status, setStatus] = useState("");

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const switchTo = useSwitchGuestMenu("preview_sheet", (to) => {
    setStatus(`Your guests now see ${versionName(to)}.`);
    onSwitched?.(to);
  });

  useEffect(() => {
    if (!status || !closeAfterSwitch) return;
    const timer = window.setTimeout(
      () => onCloseRef.current(),
      CLOSE_AFTER_SWITCH_MS
    );
    return () => window.clearTimeout(timer);
  }, [status, closeAfterSwitch]);
  const saveWith = (save: () => Promise<void>) => {
    setSaving(true);
    save().finally(() => setSaving(false));
  };

  const offerNew = !status && shown === "new" && live === "classic";
  const offerClassic = !status && shown === "classic" && live === "new";
  const caption =
    status ||
    (shown === live
      ? "What your guests see now."
      : `Preview only. Your guests still see ${versionName(live)}.`);

  return (
    <>
      <PreviewHeader
        menuName={menuUrl && (menuUrl.displayName || menuUrl.urlSlug)}
        onClose={onClose}
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "320px minmax(0, 1fr)",
          },
          overflow: { xs: "auto", md: "hidden" },
        }}
      >
        <Stack
          gap={3}
          sx={(theme) => ({
            p: 2.5,
            background: theme.palette.oscar.light.surface.neutral,
            borderRight: {
              md: `1px solid ${theme.palette.oscar.light.borders.gray}`,
            },
            overflowY: { md: "auto" },
          })}
        >
          {options.length > 1 && (
            <RailGroup label="Menu link">
              <MenuLinkPicker
                options={options}
                value={docId}
                onChange={setDocId}
              />
            </RailGroup>
          )}

          {offered && (
            <VersionList shown={shown} live={live} onChange={setShown} />
          )}

          {offered && (
            // Classic has no dark mode, so the choice goes rather than greys out.
            <Collapse
              in={shown === "new"}
              unmountOnExit
              sx={[{ mt: shown === "new" ? 0 : -3 }, reducedMotion]}
            >
              <RailGroup
                label="Appearance"
                footer="Guests see whichever their phone uses."
              >
                <SegmentedControl
                  label="Appearance"
                  value={mode}
                  onChange={setMode}
                  options={PREVIEW_MODES}
                  fullWidth
                />
              </RailGroup>
            </Collapse>
          )}
        </Stack>

        <Box
          sx={(theme) => ({
            minWidth: 0,
            minHeight: { xs: 560, md: 0 },
            background: alpha(theme.palette.common.black, 0.04),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
          })}
        >
          <PhoneFrame>
            <LazyGuestMenuVersionPreview
              version={shown}
              menuUrl={menuUrl}
              mode={mode}
              themeId={themeId}
            />
          </PhoneFrame>
        </Box>
      </Box>

      <PreviewFooter status={status} caption={caption}>
        {offerNew ? (
          <>
            <SheetButton onClick={onClose}>Not now</SheetButton>
            <SheetButton
              primary
              disabled={saving}
              onClick={() => saveWith(() => switchTo("new"))}
            >
              Use the new menu
            </SheetButton>
          </>
        ) : (
          <>
            {offerClassic && (
              <SheetButton disabled={saving} onClick={() => setSheetOpen(true)}>
                Switch to Classic
              </SheetButton>
            )}
            <SheetButton primary onClick={onClose}>
              Done
            </SheetButton>
          </>
        )}
      </PreviewFooter>

      <SwitchBackSheet
        open={sheetOpen}
        onCancel={(reason) => {
          setSheetOpen(false);
          trackSwitchBackCancelled(reason);
        }}
        onConfirm={(reason, detail) => {
          setSheetOpen(false);
          saveWith(() => switchTo("classic", reason, detail));
        }}
      />
    </>
  );
};

const PreviewHeader = ({
  menuName,
  onClose,
}: {
  menuName?: string;
  onClose: () => void;
}) => {
  const { palette } = useTheme();
  const oscar = palette.oscar.light;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      gap={2}
      sx={{
        flexShrink: 0,
        px: 3,
        pt: 2.75,
        pb: 2,
        borderBottom: `1px solid ${oscar.borders.gray}`,
      }}
    >
      <Stack gap={0.25}>
        <Typography
          id="guest-menu-preview-title"
          variant="labelLMedium"
          sx={{ fontSize: 18, lineHeight: "26px" }}
        >
          Preview your guest menu
        </Typography>
        <Typography
          variant="bodyS"
          sx={{ fontSize: 13, color: oscar.content.content2Text }}
        >
          {menuName
            ? `Your ${menuName} menu as guests see it on their phone.`
            : "Publish a menu URL to preview your menu."}
        </Typography>
      </Stack>
      <ButtonBase
        aria-label="Close"
        onClick={onClose}
        sx={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: oscar.surface.gray,
        }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <path
            d="M1 1l8 8M9 1L1 9"
            stroke={oscar.content.content2}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </ButtonBase>
    </Stack>
  );
};

const MenuLinkPicker = ({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}) => {
  const { palette } = useTheme();

  return (
    <Box
      sx={[
        rowSx,
        {
          px: 2,
          borderRadius: "12px",
          background: palette.oscar.light.surface.clear,
        },
      ]}
    >
      <FilterDropdown
        options={options}
        value={value}
        onChange={onChange}
        minWidth={120}
      />
    </Box>
  );
};

/** A labelled block in the left rail. */
const RailGroup = ({
  label,
  footer,
  children,
}: {
  label: string;
  footer?: string;
  children: React.ReactNode;
}) => {
  const { palette } = useTheme();
  const caption = {
    px: 2,
    fontSize: 13,
    color: palette.oscar.light.content.content2,
  } as const;

  return (
    <Stack gap={1}>
      <Typography variant="bodyS" sx={caption}>
        {label}
      </Typography>
      {children}
      {footer && (
        <Typography
          variant="bodyS"
          sx={{ ...caption, fontSize: 12, lineHeight: "16px" }}
        >
          {footer}
        </Typography>
      )}
    </Stack>
  );
};

const VERSIONS: ReadonlyArray<{ value: GuestMenuVersion; label: string }> = [
  { value: "new", label: "New guest menu" },
  { value: "classic", label: "Classic" },
];

/** Which menu the preview shows, as a grouped list with the live one badged. */
const VersionList = ({
  shown,
  live,
  onChange,
}: {
  shown: GuestMenuVersion;
  live: GuestMenuVersion;
  onChange: (version: GuestMenuVersion) => void;
}) => {
  const { palette } = useTheme();
  const oscar = palette.oscar.light;
  const { radioRef, onKeyDown } = useRadioArrowKeys(
    VERSIONS.map((version) => version.value),
    onChange
  );

  return (
    <Box role="radiogroup" aria-label="Show">
      <SettingsGroup header="Show">
        {VERSIONS.map((version, index) => {
          const checked = shown === version.value;
          return (
            <ButtonBase
              key={version.value}
              ref={radioRef(index)}
              role="radio"
              aria-checked={checked}
              aria-label={version.label}
              tabIndex={checked ? 0 : -1}
              onClick={() => onChange(version.value)}
              onKeyDown={(event) => onKeyDown(event, index)}
              sx={[
                rowSx,
                {
                  minHeight: 52,
                  justifyContent: "flex-start",
                  gap: 1.5,
                  textAlign: "left",
                  transition: "background 150ms ease-out",
                  "&:hover": { background: oscar.interactive.hover1 },
                  "&:first-of-type": { borderRadius: "12px 12px 0 0" },
                  "&:last-of-type": { borderRadius: "0 0 12px 12px" },
                },
                focusRing(-2),
                reducedMotion,
              ]}
            >
              <Box
                aria-hidden
                sx={[
                  {
                    flex: "0 0 20px",
                    height: 20,
                    borderRadius: "50%",
                    boxSizing: "border-box",
                    border: checked
                      ? `6px solid ${oscar.borders.electric}`
                      : `1.5px solid ${oscar.button.mid}`,
                    transition: "border 200ms ease-out",
                  },
                  reducedMotion,
                ]}
              />
              <Typography
                component="span"
                variant="bodyRRegular"
                sx={{ fontSize: 15, lineHeight: "22px" }}
              >
                {version.label}
              </Typography>
              {live === version.value && <LiveBadge />}
            </ButtonBase>
          );
        })}
      </SettingsGroup>
    </Box>
  );
};

const LiveBadge = () => {
  const { palette } = useTheme();
  const oscar = palette.oscar.light;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        height: 20,
        px: 1,
        borderRadius: "10px",
        background: oscar.surface.safe,
        color: oscar.content.accentText,
        fontSize: 12,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: oscar.content.accentText,
        }}
      />
      Live
    </Box>
  );
};

const PhoneFrame = ({ children }: { children: React.ReactNode }) => {
  const { palette } = useTheme();
  const shadow = (opacity: number) => alpha(palette.common.black, opacity);

  return (
    <Box
      sx={{
        width: 430,
        maxWidth: "100%",
        height: "100%",
        maxHeight: 720,
        borderRadius: "22px",
        overflow: "hidden",
        background: palette.oscar.light.surface.clear,
        boxShadow: `0 1px 3px ${shadow(0.08)}, 0 12px 32px ${shadow(0.1)}`,
      }}
    >
      {children}
    </Box>
  );
};

const PreviewFooter = ({
  status,
  caption,
  children,
}: {
  status: string;
  caption: string;
  children: React.ReactNode;
}) => {
  const { palette } = useTheme();
  const oscar = palette.oscar.light;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      flexWrap="wrap"
      sx={{
        flexShrink: 0,
        px: 3,
        py: 2,
        borderTop: `1px solid ${oscar.borders.gray}`,
      }}
    >
      <Stack
        role="status"
        direction="row"
        alignItems="center"
        gap={1}
        // Replays the entrance when the switch lands.
        key={status ? "switched" : "preview"}
        sx={[
          status
            ? {
                "@keyframes switchedIn": {
                  from: { opacity: 0, transform: "translateY(4px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: "switchedIn 250ms ease-out",
              }
            : {},
          reducedMotion,
        ]}
      >
        {status && <SwitchedTick />}
        <Typography
          variant="bodyS"
          sx={{
            fontSize: 13,
            color: status
              ? oscar.content.accentText
              : oscar.content.content2Text,
          }}
        >
          {caption}
        </Typography>
      </Stack>
      <Stack direction="row" gap={1.25}>
        {children}
      </Stack>
    </Stack>
  );
};

const SwitchedTick = () => {
  const { palette } = useTheme();
  const oscar = palette.oscar.light;

  return (
    <Box
      aria-hidden
      sx={[
        {
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: oscar.content.accentText,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "@keyframes tickIn": {
            "0%": { transform: "scale(0.4)" },
            "70%": { transform: "scale(1.12)" },
            "100%": { transform: "scale(1)" },
          },
          animation: "tickIn 350ms ease-out",
        },
        reducedMotion,
      ]}
    >
      <Tick size={11} color={oscar.surface.clear} strokeWidth={2.2} />
    </Box>
  );
};
